// HelpHint — the "?" beside a value, a click/focus/hover toggletip. Pure.
//
// Cryptic values and chips (LIX, Källöverlapp, Förankrat) need explain-in-
// place help. This is that primitive (inline-help.md): a small CircleHelp
// button that opens a short explanation. It is a TOGGLETIP, not a tooltip —
// the content is informational, requested by the user, so it is announced on
// activation (Radix renders the panel as role="dialog" with an accessible
// name and focuses it on click/keyboard open) rather than tied via
// aria-describedby (which would read the description before the user asks).
//
// WCAG 1.4.13 (content on hover/focus) is satisfied by the Radix Popover:
// the opened content is DISMISSIBLE (Esc / outside click — Radix), HOVERABLE
// (the pointer can travel onto the bubble — we keep it open while the pointer
// is over trigger or content), and PERSISTENT (stays until dismissed). Hover-
// open is a mouse affordance layered on top after a short delay; it never
// steals focus (onOpenAutoFocus is prevented for hover-opens), so a pointer
// glance stays a glance. Touch and keyboard use the button's native activation.
//
// Pure/props-driven: `label` is the caller-localised accessible name (the
// dialog's name, required — an unnamed dialog is an axe error); `children` is
// the explanation (≤2 sentences, human register, caller-localised via Trans);
// `learnMoreHref` renders an optional "Läs mer" link. The only state is local
// presentation state (open + hover timers) — no I/O, no domain knowledge.

import { Trans } from "@lingui/react/macro";
import * as RadixPopover from "@radix-ui/react-popover";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Link } from "../Link/Link.js";
import type { PopoverAlign, PopoverSide } from "../Popover/Popover.js";
import "../Popover/Popover.css";
import "./HelpHint.css";

// Pointer-hover delays: open only after a deliberate pause (not on a passing
// sweep); close after a short grace so the pointer can travel to the bubble.
const HOVER_OPEN_DELAY_MS = 300;
const HOVER_CLOSE_DELAY_MS = 150;
const SIDE_OFFSET = 6;

// A circled question mark — the muted discoverability glyph beside the value.
const helpGlyph = <Icon name="help" />;

export type HelpHintProps = {
    // The dialog's accessible name (required — Radix content is role="dialog";
    // an unnamed dialog fails axe). Caller-localised, e.g. t`Explanation: LIX`.
    readonly label: string;
    // The explanation — ≤2 sentences, human register, caller-localised.
    readonly children: ReactNode;
    // Optional deeper link; renders a trailing "Läs mer" only when given.
    readonly learnMoreHref?: string;
    readonly side?: PopoverSide;
    readonly align?: PopoverAlign;
    readonly className?: string;
};

export const HelpHint = ({
    label,
    children,
    learnMoreHref,
    side = "top",
    align = "center",
    className,
}: HelpHintProps) => {
    const [open, setOpen] = useState(false);
    // Was the current open a pointer hover? Then suppress the focus-on-open so
    // a mouse glance never steals focus; click/keyboard opens keep it (for the
    // announcement). A ref, not state — it must not trigger a re-render.
    const hoverOpened = useRef(false);
    const openTimer = useRef<number | undefined>(undefined);
    const closeTimer = useRef<number | undefined>(undefined);

    const cancelTimers = () => {
        window.clearTimeout(openTimer.current);
        window.clearTimeout(closeTimer.current);
    };
    // Clear any pending hover timer on unmount (reads only refs — stable).
    useEffect(
        () => () => {
            window.clearTimeout(openTimer.current);
            window.clearTimeout(closeTimer.current);
        },
        [],
    );

    // Pointer entered trigger or content — open after the pause (cancel any
    // pending close so travelling between the two never flickers shut).
    const onHoverEnter = () => {
        window.clearTimeout(closeTimer.current);
        openTimer.current = window.setTimeout(() => {
            hoverOpened.current = true;
            setOpen(true);
        }, HOVER_OPEN_DELAY_MS);
    };
    // Pointer left — close after a short grace so a travel to the bubble wins.
    const onHoverLeave = () => {
        window.clearTimeout(openTimer.current);
        closeTimer.current = window.setTimeout(
            () => setOpen(false),
            HOVER_CLOSE_DELAY_MS,
        );
    };

    return (
        <RadixPopover.Root
            open={open}
            onOpenChange={(next) => {
                // A click/keyboard/Esc/outside request — not a hover. Cancel any
                // hover timer and let this open take focus (announcement).
                cancelTimers();
                if (next) hoverOpened.current = false;
                setOpen(next);
            }}
        >
            <RadixPopover.Trigger asChild>
                <IconButton
                    label={label}
                    variant="ghost"
                    size="sm"
                    className={`bilda-help-hint__trigger${
                        className ? ` ${className}` : ""
                    }`}
                    onPointerEnter={onHoverEnter}
                    onPointerLeave={onHoverLeave}
                >
                    {helpGlyph}
                </IconButton>
            </RadixPopover.Trigger>
            <RadixPopover.Portal>
                <RadixPopover.Content
                    className="bilda-popover bilda-help-hint"
                    aria-label={label}
                    side={side}
                    align={align}
                    sideOffset={SIDE_OFFSET}
                    onOpenAutoFocus={(event) => {
                        if (hoverOpened.current) event.preventDefault();
                    }}
                    onPointerEnter={() =>
                        window.clearTimeout(closeTimer.current)
                    }
                    onPointerLeave={onHoverLeave}
                >
                    <div className="bilda-help-hint__body">{children}</div>
                    {learnMoreHref !== undefined ? (
                        <Link
                            href={learnMoreHref}
                            className="bilda-help-hint__more"
                        >
                            <Trans>Learn more</Trans>
                        </Link>
                    ) : null}
                    <RadixPopover.Arrow className="bilda-popover__arrow" />
                </RadixPopover.Content>
            </RadixPopover.Portal>
        </RadixPopover.Root>
    );
};
